import { NodeToolbar, Position, useNodeId } from "@xyflow/react";
import classNames from "classnames";
import { Icon } from "@zapier/design-system";
import { Tooltip } from "@zapier/design-system-beta";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import {
  ActiveButtonType,
  Actor,
  floatingToolbarActiveButtonAtom,
  NodeType,
  Service,
  sidebarOpenNodeIdAtom,
  ZapMetadata,
} from "@zapier/canvas-state";
import styles from "./FloatingInlineToolbar.module.css";
import { FloatingActorTypeahead } from "./FloatingActorTypeahead";
import { FloatingAppTypeahead } from "./FloatingAppTypeahead";
import { FloatingZapTypeahead } from "./FloatingZapTypeahead";
import { FloatingColorPicker } from "./FloatingColorPicker";

type Props = {
  color?: string;
  showAddAppButton?: boolean;
  selectedApps?: Service[];
  actors: Actor[] | undefined;
  hasZap?: boolean;
  isZapNotFound?: boolean;
  zap?: ZapMetadata;
  nodeType: NodeType;
};

export function FloatingInlineToolbar({
  color,
  showAddAppButton = true,
  selectedApps,
  actors,
  hasZap,
  isZapNotFound,
  zap,
}: Props) {
  const id = useNodeId();
  const hasApp = Boolean(selectedApps && selectedApps.length > 0);
  const setSidebarOpenNodeId = useSetAtom(sidebarOpenNodeIdAtom);
  const [activeButton, setActiveButton] = useAtom(
    floatingToolbarActiveButtonAtom,
  );
  const actor = actors?.[0] ?? null;

  useEffect(() => {
    return () => {
      setActiveButton(null);
    };
  }, [setActiveButton]);

  const openSidebar = useCallback(() => {
    if (id !== null) {
      setSidebarOpenNodeId(id);
    }
  }, [setSidebarOpenNodeId, id]);

  const onButtonClick = useCallback(
    (type: ActiveButtonType) => setActiveButton(type),
    [setActiveButton],
  );

  const componentMapByType = {
    color: <FloatingColorPicker color={color!} />,
    zap: (
      <FloatingZapTypeahead
        zapTitle={zap?.title}
        hasZap={hasZap}
        isZapNotFound={isZapNotFound}
      />
    ),
    app: <FloatingAppTypeahead selectedApps={selectedApps} />,
    actor: <FloatingActorTypeahead actors={actors} />,
  };

  return (
    <NodeToolbar position={Position.Top} offset={25}>
      {activeButton ? (
        componentMapByType[activeButton]
      ) : (
        <div className={styles.floatingToolbarWrapper}>
          {color && (
            <Tooltip placement="top" content="Edit color">
              <Tooltip.FocusableTrigger>
                <button
                  className={styles.buttonNoStyle}
                  onClick={() => onButtonClick("color")}
                >
                  <div
                    className={styles.colorDot}
                    style={{ backgroundColor: color }}
                  />
                </button>
              </Tooltip.FocusableTrigger>
            </Tooltip>
          )}
          <Tooltip placement="top" content={`${hasZap ? "Edit" : "Add"} Zap`}>
            <Tooltip.FocusableTrigger>
              <button
                className={classNames(styles.buttonNoStyle, {
                  [styles.hasZap]: hasZap,
                })}
                onClick={() => onButtonClick("zap")}
              >
                <Icon
                  name={hasZap ? "miscBoltAltFill" : "miscBoltAlt"}
                  size={20}
                />
              </button>
            </Tooltip.FocusableTrigger>
          </Tooltip>
          {showAddAppButton && (
            <Tooltip placement="top" content={`${hasApp ? "Edit" : "Add"} app`}>
              <Tooltip.FocusableTrigger>
                <button
                  className={styles.buttonNoStyle}
                  onClick={() => onButtonClick("app")}
                >
                  <Icon name="navApps" size={20} />
                </button>
              </Tooltip.FocusableTrigger>
            </Tooltip>
          )}
          <Tooltip
            placement="top"
            content={`${actor ? "Edit" : "Add"} people involved`}
          >
            <Tooltip.FocusableTrigger>
              <button
                className={styles.buttonNoStyle}
                onClick={() => onButtonClick("actor")}
              >
                <Icon name="personCircle" size={20} />
              </button>
            </Tooltip.FocusableTrigger>
          </Tooltip>
          <button
            className={`${styles.buttonNoStyle} ${styles.detailsButton}`}
            onClick={openSidebar}
          >
            <span className={styles.text}>Details</span>
            <Icon name="arrowSmallRight" size={20} />
          </button>
        </div>
      )}
    </NodeToolbar>
  );
}
